/**
 * O projeto aberto: faixa com o projeto e as abas.
 *
 * A barra tem as abas de cadastro de conteúdo (ver `abasDoProjeto`) e, no fim,
 * o "⋯" com o resto do projeto — Projeto, Cadastros, Sistemas, Modelos, Checklist.
 *
 * As abas se escondem por chave de acesso, como o `painel.Acessar` já faz no
 * legado. As que ainda não existem nesta volta aparecem desligadas com o
 * motivo, em vez de sumirem — quem usa o sistema hoje precisa reconhecer o
 * lugar delas.
 */

import { NavLink, Outlet, useLocation, useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { obter } from "@/api/cliente"
import { chaves } from "@/api/query"
import { MOSTRAR_GRAFICO_DESATUALIZADO, type ProjetoResponse } from "@/api/tipos"
import { Faixa } from "@/kit/Faixa"
import { usarSessao } from "@/sessao/Sessao"
import { abasDoProjeto, maisDoProjeto } from "@/casca/navegacao"
import { MaisDoProjeto } from "./projeto/MaisDoProjeto"

export function Projeto() {
  const { projetoId } = useParams()
  const id = Number(projetoId)
  const { pode } = usarSessao()
  const abas = abasDoProjeto.filter((a) => !a.chave || pode(a.chave))
  const mais = maisDoProjeto.filter((a) => !a.chave || pode(a.chave))
  // Qual tela do "⋯" está aberta, se alguma: o botão mostra o nome dela, aceso.
  const { pathname } = useLocation()
  const trecho = pathname.split("/")[3] ?? ""
  const doMais = mais.find((i) => i.para === trecho)

  const { data: projeto } = useQuery({
    queryKey: chaves.projeto(id),
    enabled: Number.isFinite(id) && id > 0,
    queryFn: () => obter<ProjetoResponse>(`api/projetos/${id}`),
  })

  return (
    <>
      <Faixa
        titulo={projeto?.descricao ?? "Projeto"}
        codigo={projetoId}
        detalhe={projeto ? <>{projeto.categoria}</> : "carregando…"}
      >
        {MOSTRAR_GRAFICO_DESATUALIZADO && projeto?.atualizar && (
          <span
            title="A RN-05 marcou o projeto: houve escrita de pinout depois da última geração."
            style={{
              display: "inline-flex", alignItems: "center", gap: 6, height: 22, padding: "0 9px",
              borderRadius: 999, background: "var(--acao-soft)", color: "var(--acao-on-soft)",
              fontSize: 11, fontWeight: 600,
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
                 style={{ fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }}>
              <path d="M12 4 2.8 20h18.4L12 4Z" /><path d="M12 10.2v4M12 17.2v.4" />
            </svg>
            gráfico desatualizado
          </span>
        )}
      </Faixa>

      <div
        style={{
          height: 38, flex: "0 0 38px", display: "flex", alignItems: "stretch", gap: 2,
          padding: "0 14px", background: "var(--surface2)", borderBottom: "1px solid var(--line2)",
        }}
      >
        {abas.map((aba) =>
          aba.emBreve ? (
            <span
              key={aba.para}
              title="Ainda não nesta volta — ver o calendário no plano de execução"
              style={{ display: "flex", alignItems: "center", padding: "0 12px", fontSize: 12.5, color: "var(--txt3)" }}
            >
              {aba.rotulo}
            </span>
          ) : (
            <NavLink
              key={aba.para}
              to={aba.para}
              style={({ isActive }) => ({
                position: "relative",
                display: "flex",
                alignItems: "center",
                padding: "0 12px",
                fontSize: 12.5,
                fontWeight: isActive ? 600 : 500,
                color: isActive ? "var(--txt)" : "var(--txt2)",
                boxShadow: isActive ? "inset 0 -2px 0 var(--brand)" : "none",
              })}
            >
              {aba.rotulo}
            </NavLink>
          ),
        )}
        <MaisDoProjeto projetoId={id} itens={mais} aberto={doMais} />
      </div>

      <Outlet />
    </>
  )
}
